import React from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, Trash2, X, RefreshCw } from "lucide-react";
import Button from "../../../../components/common/Button";

export interface DeleteConfirmModalProps {
  isOpen: boolean;
  title?: string;
  description?: string;
  itemName?: string;
  itemType?: string;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  isOpen,
  title = "Confirm Deletion",
  description,
  itemName,
  itemType = "item",
  loading = false,
  onConfirm,
  onClose,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="cht-fixed cht-inset-0 cht-w-screen cht-h-screen cht-min-h-screen cht-bg-slate-900/60 cht-backdrop-blur-xs cht-z-[99999] cht-flex cht-items-center cht-justify-center cht-p-4 cht-overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 10 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="cht-bg-white cht-rounded-[24px] cht-p-6 cht-max-w-sm cht-w-full cht-shadow-2xl cht-space-y-4 cht-border cht-border-gray-100 cht-text-center cht-my-auto"
        >
          {/* Warning Icon Badge */}
          <div className="cht-h-14 cht-w-14 cht-rounded-2xl cht-bg-rose-50 cht-text-rose-600 cht-flex cht-items-center cht-justify-center cht-mx-auto cht-ring-8 cht-ring-rose-50/50">
            <AlertTriangle className="cht-h-7 cht-w-7" />
          </div>

          <div className="cht-space-y-1.5">
            <h3 className="cht-text-lg cht-font-black cht-text-[#1e293b]">{title}</h3>
            <p className="cht-text-xs cht-text-gray-500 cht-leading-relaxed">
              {description || (
                <>
                  Are you sure you want to delete{" "}
                  {itemName ? (
                    <strong className="cht-text-[#1e293b] cht-font-black">"{itemName}"</strong>
                  ) : (
                    `this ${itemType}`
                  )}
                  ? This action cannot be undone.
                </>
              )}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="cht-grid cht-grid-cols-2 cht-gap-3 cht-pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="cht-w-full cht-text-xs cht-font-bold cht-rounded-xl"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={onConfirm}
              isLoading={loading}
              disabled={loading}
              leftIcon={<Trash2 className="cht-h-3.5 cht-w-3.5" />}
              className="cht-w-full cht-text-xs cht-font-bold cht-rounded-xl"
            >
              Delete
            </Button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};
